import { ApiError, type ApiClientConfig, type RequestOptions } from "./types";

let activeConfig: ApiClientConfig = { baseUrl: "" };

let refreshInFlight: Promise<boolean> | null = null;

export function configureApiClient(patch: Partial<ApiClientConfig>): void {
  activeConfig = { ...activeConfig, ...patch };
}

export function getApiBaseUrl(): string {
  return activeConfig.baseUrl;
}

async function buildHeaders(init: HeadersInit | undefined, hasBody: boolean): Promise<Headers> {
  const headers = new Headers(init);
  if (hasBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (activeConfig.getBearerToken) {
    const token = await activeConfig.getBearerToken();
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  if (activeConfig.getRequestHeaders) {
    const extra = await activeConfig.getRequestHeaders();
    for (const [name, value] of Object.entries(extra ?? {})) {
      if (!headers.has(name)) headers.set(name, value);
    }
  }
  return headers;
}

async function attemptRefresh(): Promise<boolean> {
  if (!activeConfig.refreshAccessToken) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      return await activeConfig.refreshAccessToken!();
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

interface RawRequest {
  method: string;
  path: string;
  body?: unknown;
  options?: RequestOptions;
}

// Idempotent verbs that are safe to retry blindly on transient backend
// failure. POST is intentionally included — for /api/auth/refresh and a
// handful of other "conceptually idempotent" mutations the server is the
// authority on duplicate detection (refresh-token grace, OTP rate-limit,
// etc), so retrying once is strictly better than dropping the request.
const RETRYABLE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "POST"]);
const TRANSIENT_STATUS = new Set([502, 503, 504]);
// Backoff schedule sized for the worst dev case: a full `tsx watch` restart
// of apps/api after a TypeScript reload, which can take 5–8s end-to-end on
// a cold cache. Total budget here is ~7.7s spread across 5 retries so the
// SPA survives the whole restart window without bubbling 502s up to the
// user. Production gets the same budget — a real Fly machine cycle is
// 1–3s, so this just means a slow rolling deploy is invisible too.
const TRANSIENT_BACKOFF_MS = [200, 500, 1000, 2000, 4000];

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

async function send(req: RawRequest, isRetry = false): Promise<Response> {
  const hasBody = req.body !== undefined;
  const headers = await buildHeaders(req.options?.headers, hasBody);
  const init: RequestInit = {
    method: req.method,
    credentials: "include",
    headers,
    signal: req.options?.signal,
    body: hasBody ? JSON.stringify(req.body) : undefined,
  };
  const url = `${activeConfig.baseUrl}${req.path}`;
  const canRetryTransient = RETRYABLE_METHODS.has(req.method);

  // Transient-failure retry. Covers two dev-path scenarios that previously
  // cascaded into spurious logout:
  //   1. apps/api restarting under tsx watch → Caddy returns 502 with no CORS
  //      headers → fetch resolves to a 502 Response.
  //   2. apps/api dropping the connection mid-restart → fetch THROWS a
  //      TypeError("Failed to fetch") or AbortError. Without retry we'd
  //      surface this as a generic auth/network failure to the SPA.
  // Production: same logic, just rarer (Fly machine cycle, transient L4 hiccup).
  let res: Response | null = null;
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= TRANSIENT_BACKOFF_MS.length; attempt++) {
    try {
      res = await fetch(url, init);
      lastError = null;
      if (!canRetryTransient || !TRANSIENT_STATUS.has(res.status)) break;
    } catch (err) {
      // Don't retry user-cancelled requests — propagate the abort.
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      lastError = err;
      if (!canRetryTransient) throw err;
    }
    if (attempt >= TRANSIENT_BACKOFF_MS.length) break;
    await sleep(TRANSIENT_BACKOFF_MS[attempt]!);
  }
  if (!res) {
    // All attempts threw. Surface the last error so the caller can branch on it.
    throw lastError ?? new Error("network request failed");
  }
  if (
    res.status === 401 &&
    !isRetry &&
    !req.options?.skipAuthRefresh &&
    !req.path.startsWith("/api/auth/")
  ) {
    const refreshed = await attemptRefresh();
    if (refreshed) return send(req, true);
    activeConfig.onAuthFailure?.();
  }
  return res;
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      // body might be empty or non-JSON
    }
    throw new ApiError(res.status, parsed);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("Content-Type") ?? "";
  if (!ct.includes("application/json")) return undefined as T;
  const body: unknown = await res.json();
  if (activeConfig.onResponseBody) {
    try {
      await activeConfig.onResponseBody(body);
    } catch {
      // A hook's failure never fails the call it watched.
    }
  }
  return body as T;
}

export const apiFetch = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return send({ method: "GET", path, options }).then((r) => parseOrThrow<T>(r));
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return send({ method: "POST", path, body, options }).then((r) => parseOrThrow<T>(r));
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return send({ method: "PUT", path, body, options }).then((r) => parseOrThrow<T>(r));
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return send({ method: "PATCH", path, body, options }).then((r) => parseOrThrow<T>(r));
  },
  /**
   * DELETE, optionally with a JSON body. Several endpoints take their target in
   * the body rather than the query string; without this overload callers fell
   * back to `raw()` and then forgot to check `res.ok`, so a 401/412 read as
   * success and the UI removed a row the server had kept.
   */
  delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return send({ method: "DELETE", path, body, options }).then((r) => parseOrThrow<T>(r));
  },
  /** Escape hatch — direct fetch for streams, FormData uploads, ICS downloads, etc. */
  raw(path: string, init: RequestInit): Promise<Response> {
    return fetch(`${activeConfig.baseUrl}${path}`, {
      credentials: "include",
      ...init,
    });
  },
};
